import Button from '../../../components/base/Button';
import heroImage from '../../../assets/hero.png';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mobileReveal, setMobileReveal] = useState(0);

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 640px)').matches;
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = sectionRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight || 1;

        // 画面上部に来たらせり上がり開始、少しスクロールしたら完了
        const start = viewportH * 0.25; // rect.top がこの位置に来たら開始
        const end = viewportH * -0.15;  // rect.top がここまで上がったら完了
        const t = (start - rect.top) / (start - end);
        const clamped = Math.min(1, Math.max(0, t));
        setMobileReveal(clamped);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isMobile]);

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${heroImage}')`,
        backgroundPosition: 'center 20%',
      }}
    >
      {/* PC/タブレット: 従来どおりセンター表示 */}
      <div className="hidden sm:block container mx-auto px-6 text-center text-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            久下暁雅<br />
            <span className="text-3xl md:text-4xl font-light text-blue-200">ーー蒼き炎、魂の叫び</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto leading-relaxed">
            小学生の頃、一本のギターが運命を変えた。<br />
            ロックスターへの憧れ、バンド活動、俳優としての歩み。<br />
            そして2025年秋、「再起動」の3文字と共に新たな物語が始まる。<br />
            寡黙な青年が情感たっぷりに歌い上げるバラードに<br />
            俳優として培った表現力が宿り<br />
            迷い悩む心の奥底を歌声に込めて、あなたの魂に深く響く。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg">
              <i className="ri-play-circle-line mr-2 text-xl"></i>
              最新楽曲を聴く
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black">
              <i className="ri-calendar-line mr-2 text-xl"></i>
              ライブ情報
            </Button>
          </div>
        </div>
      </div>

      {/* スマホ: 画像を優先し、スクロールでテキストが下からせり上がる */}
      <div
        className="sm:hidden absolute inset-x-0 bottom-0 px-4 pb-8"
        style={{
          transform: `translateY(${Math.round((1 - mobileReveal) * 70)}%)`,
          opacity: mobileReveal,
          transition: 'transform 0.05s linear, opacity 0.05s linear',
        }}
      >
        <div className="mx-auto max-w-md rounded-2xl bg-black/55 backdrop-blur-md p-5 text-white shadow-2xl">
          <h1 className="text-4xl font-bold leading-tight">
            久下暁雅
            <span className="block mt-2 text-lg font-light text-blue-200">ーー蒼き炎、魂の叫び</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-gray-100">
            小学生の頃、一本のギターが運命を変えた。ロックスターへの憧れ、バンド活動、俳優としての歩み。
            そして2025年秋、「再起動」の3文字と共に新たな物語が始まる。
          </p>
          <div className="mt-5 flex flex-col gap-3">
            <Button size="lg" className="w-full bg-blue-600 text-white hover:bg-blue-700 shadow-lg">
              <i className="ri-play-circle-line mr-2 text-xl"></i>
              最新楽曲を聴く
            </Button>
            <Button variant="outline" size="lg" className="w-full border-white text-white hover:bg-white hover:text-black">
              <i className="ri-calendar-line mr-2 text-xl"></i>
              ライブ情報
            </Button>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white text-2xl"></i>
      </div>
    </section>
  );
}
